import { cloneElement, ComponentProps } from 'react';
import { Modal as RSModal } from 'reactstrap';
import { Content } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Button, Stack, Tooltip } from 'src/core/ui';
import { SuppressCircuitBreakerDialog } from 'src/dialogs/SuppressCircuitBreakerDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  IconsFill,
  WarningOutlineIcon,
  WarningSolidIcon,
} from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { AUTOPRICING_RESULT_STATE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  AutoPricingConstraintType,
  AutoPricingResultType,
  Feature,
} from 'src/WebApiController';

import * as styles from './AutoPriceResultStateBadge.css';
import { hasActionSupport } from './autoPriceResultStateUtils';

export type AutoPriceResultStateActionGenericDialogProps = ComponentProps<
  typeof RSModal
> & {
  listingId?: number;
  autoPriceResultState: AutoPricingResultType;
  onCancelClick: () => void;
};

const AutoPriceResultStateActionGenericDialog = ({
  listingId,
  autoPriceResultState,
  onCancelClick,
  ...rest
}: AutoPriceResultStateActionGenericDialogProps) => {
  switch (autoPriceResultState) {
    case AutoPricingResultType.PriceHitMaxAllowedDrop:
      return listingId ? (
        <SuppressCircuitBreakerDialog
          {...rest}
          listingId={listingId}
          onCancelClick={onCancelClick}
        />
      ) : null;

    default:
      return null;
  }
};

export const AutoPriceResultStateBadge = ({
  disabled,
  listingId,
  autoPricingResultType,
  autoPricingResultConstraintType,
  shouldShowResultTypeAction,
}: {
  disabled?: boolean;
  listingId?: number;
  autoPricingResultType?: AutoPricingResultType;
  autoPricingResultConstraintType?: AutoPricingConstraintType;
  shouldShowResultTypeAction?: boolean;
}) => {
  const { isDarkMode } = useSiteTheme();

  const autoPriceResultStateGenericDialog = useBasicDialog();

  const hasAutoPricingIgnoreVeloCBFeature = useUserHasFeature(
    Feature.AutoPricingIgnoreVeloCB
  );

  const icon =
    autoPricingResultConstraintType ===
    AutoPricingConstraintType.HardConstraint ? (
      <WarningSolidIcon />
    ) : autoPricingResultConstraintType ===
      AutoPricingConstraintType.SoftConstraint ? (
      <WarningOutlineIcon />
    ) : null;

  if (!icon || !autoPricingResultType || !autoPricingResultConstraintType) {
    return null;
  }

  const styledIcon = cloneElement(icon, {
    size: vars.iconSize.m,
    fill: disabled ? IconsFill.textDisabled : IconsFill.currentColor,
  });

  const badgeStyle =
    autoPricingResultConstraintType === AutoPricingConstraintType.HardConstraint
      ? styles.autoPriceResultHardConstraintBadge
      : styles.autoPriceResultSoftConstraintBadge;

  const content = (
    <Stack alignItems="center" className={badgeStyle}>
      {styledIcon}
    </Stack>
  );

  const postfixDisplay = hasActionSupport(autoPricingResultType) ? (
    <Button
      variant="textPlain"
      size="unset"
      onClick={(e) => {
        e.stopPropagation();
        autoPriceResultStateGenericDialog.launchDialog();
      }}
      textColor="inverted"
    >
      <Content id={ContentId.Edit} />
    </Button>
  ) : undefined;

  return (
    <>
      <Tooltip
        triggerContent={content}
        contentVariant={isDarkMode ? 'light' : 'dark'}
        postfixDisplay={
          hasAutoPricingIgnoreVeloCBFeature && shouldShowResultTypeAction
            ? postfixDisplay
            : undefined
        }
      >
        <Content id={AUTOPRICING_RESULT_STATE_TO_CID[autoPricingResultType]} />
      </Tooltip>
      {hasAutoPricingIgnoreVeloCBFeature &&
        hasActionSupport(autoPricingResultType) && (
          <AutoPriceResultStateActionGenericDialog
            {...autoPriceResultStateGenericDialog.dialogProps}
            listingId={listingId}
            autoPriceResultState={autoPricingResultType}
            onCancelClick={autoPriceResultStateGenericDialog.closeDialog}
          />
        )}
    </>
  );
};
